import { ChakraProvider } from "@chakra-ui/react";
import {
  createRouter,
  ParsedLocation,
  RouterProvider,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { useBrandingContext } from "./branding/useBrandingContext";
import { ColorModeConfiguration } from "./ColorModeConfiguration";
import { queryClient } from "./queryClient";
import { routeTree } from "./routeTree.gen";
import { brandTheme } from "./theme/theme";
import { setupSentry } from "./utils/sentry";

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  scrollToTopSelectors: ["#main-scrollable-area"],
  // let them bubble to vite's overlay during development
  disableGlobalCatchBoundary: process.env.NODE_ENV === "development",
});

setupSentry(router);

// Declare the router to TypeScript
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    from?: ParsedLocation["href"];
    error?: string;
    latencyCheckAborted?: boolean;
  }
}

export default function App() {
  const branding = useBrandingContext();

  const theme = useMemo(
    () =>
      brandTheme(
        branding.primary ?? undefined,
        branding.background ?? undefined,
      ),
    [branding.primary, branding.background],
  );

  return (
    <ChakraProvider theme={theme}>
      <ColorModeConfiguration />
      {branding.product_icon && (
        <link rel="icon" type="image/png" href={branding.product_icon} />
      )}
      <meta name="theme-color" content={theme.colors.background["500"]} />
      <RouterProvider router={router} />
    </ChakraProvider>
  );
}
